import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { ApiError, ApiRequestConfig, ApiResponse } from './types';

// Default configuration
const DEFAULT_API_CONFIG: ApiRequestConfig = {
  timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 10000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  retry: true,
  retryAttempts: Number(import.meta.env.VITE_API_RETRY_ATTEMPTS) || 3,
  retryDelay: Number(import.meta.env.VITE_API_RETRY_DELAY) || 1000,
};

class ApiClient {
  private instance: AxiosInstance;

  constructor(config: ApiRequestConfig = {}) {
    this.instance = axios.create({
      ...DEFAULT_API_CONFIG,
      ...config,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request Interceptor
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Add any auth tokens or common headers here if needed
        return config;
      },
      error => Promise.reject(error)
    );

    // Response Interceptor
    this.instance.interceptors.response.use(
      response => response,
      async (error: AxiosError) => {
        const config = error.config as ApiRequestConfig;

        // Handle retry logic
        if (config && config.retry && (config.retryAttempts || 0) > 0) {
          config.retryAttempts = (config.retryAttempts || 0) - 1;

          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, config.retryDelay || 1000));

          return this.instance(config);
        }

        return Promise.reject(this.normalizeError(error));
      }
    );
  }

  private normalizeError(error: AxiosError): ApiError {
    const apiError: ApiError = {
      code: error.code || 'UNKNOWN_ERROR',
      message: error.message || 'An unexpected error occurred',
      status: error.response?.status,
    };

    if (error.response?.data) {
      const data = error.response.data as any;
      apiError.message = data.message || apiError.message;
      apiError.details = data.details || data;
    }

    return apiError;
  }

  // Generic request methods
  public async get<T>(url: string, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.get<T>(url, config);
    return {
      data: response.data,
      status: response.status,
      timestamp: new Date().toISOString(),
    };
  }

  public async post<T>(
    url: string,
    data?: any,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.post<T>(url, data, config);
    return {
      data: response.data,
      status: response.status,
      timestamp: new Date().toISOString(),
    };
  }

  public async put<T>(url: string, data?: any, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.put<T>(url, data, config);
    return {
      data: response.data,
      status: response.status,
      timestamp: new Date().toISOString(),
    };
  }

  public async delete<T>(url: string, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.delete<T>(url, config);
    return {
      data: response.data,
      status: response.status,
      timestamp: new Date().toISOString(),
    };
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export class for custom instances
export default ApiClient;
